<div class="verified-dashboard">
  <ng-container *ngIf="dashboard$ | async as dashboard; else initialState">
    <div class="refresh-progress" *ngIf="isLoading$ | async">
      <fs-loading-spinner [variant]="LOADING_STYLE.CIRCLE_GRAY"></fs-loading-spinner>
      <span>{{ 'dashboard_refreshing' | translate }}</span>
    </div>

    <div class="error-banner" *ngIf="hasError$ | async" role="alert">
      <mat-icon>cloud_off</mat-icon>
      <div>
        <strong>{{ 'dashboard_error_title' | translate }}</strong>
        <span>{{ 'dashboard_error_description' | translate }}</span>
      </div>
      <button mat-stroked-button type="button" (click)="refresh()">
        <mat-icon>refresh</mat-icon>
        {{ 'dashboard_retry' | translate }}
      </button>
    </div>

    <header class="dashboard-header">
      <div class="welcome-copy">
        <div class="eyebrow">{{ 'dashboard_overview' | translate }}</div>
        <h1>
          {{ 'dashboard_welcome' | translate }}
          <span>{{ (restaurantData$ | async)?.name }}</span>
        </h1>
        <p>
          {{ 'dashboard_welcome_description' | translate }}
        </p>
        <div class="updated-at">
          <mat-icon>schedule</mat-icon>
          {{ 'dashboard_updated_at' | translate }}
          {{ formatTimestamp(dashboard.generatedAt, dashboard.timeZone, 'dateTime') }}
          <ng-container *ngIf="dashboard.timeZone"> &middot; {{ dashboard.timeZone }}</ng-container>
        </div>
      </div>

      <div class="dashboard-controls">
        <mat-button-toggle-group
          [value]="selectedPeriodDays$ | async"
          (change)="onPeriodChange($event.value)"
          [attr.aria-label]="'dashboard_period_selector' | translate"
        >
          <mat-button-toggle *ngFor="let period of availablePeriods" [value]="period">
            {{ period }} {{ 'dashboard_days_short' | translate }}
          </mat-button-toggle>
        </mat-button-toggle-group>

        <fs-button
          [text]="'dashboard_refresh' | translate"
          [type]="'raised-primary'"
          [leadingIcon]="'refresh'"
          [disabled]="(isLoading$ | async) || false"
          (onPress)="refresh()"
        ></fs-button>
      </div>
    </header>

    <section class="today-strip" [attr.aria-label]="'dashboard_today' | translate">
      <div>
        <span>{{ 'dashboard_today_received' | translate }}</span>
        <strong>{{ dashboard.today.receivedOrderCount }}</strong>
      </div>
      <div>
        <span>{{ 'dashboard_today_completed' | translate }}</span>
        <strong>{{ dashboard.today.completedOrderCount }}</strong>
      </div>
      <div>
        <span>{{ 'dashboard_today_completed_value' | translate }}</span>
        <strong>{{ dashboard.today.completedOrderValueCents | format_cents: true }}</strong>
      </div>
      <div>
        <span>{{ 'dashboard_active_now' | translate }}</span>
        <strong>{{ dashboard.activeOrderCount }}</strong>
      </div>
    </section>

    <section class="kpi-grid" [attr.aria-label]="'dashboard_key_metrics' | translate">
      <article class="kpi-card primary">
        <div class="kpi-icon"><mat-icon>payments</mat-icon></div>
        <div class="kpi-label">{{ 'dashboard_kpi_completed_value' | translate }}</div>
        <div class="kpi-value">
          {{ dashboard.currentPeriod.completedOrderValueCents | format_cents: true }}
        </div>
        <div
          class="kpi-comparison"
          [ngClass]="getMetricTrendClass(
            dashboard.currentPeriod.completedOrderValueCents,
            dashboard.previousPeriod.completedOrderValueCents
          )"
        >
          <ng-container
            *ngIf="
              getMetricChange(
                dashboard.currentPeriod.completedOrderValueCents,
                dashboard.previousPeriod.completedOrderValueCents
              ) !== null;
              else newCompletedValue
            "
          >
            <mat-icon>
              {{
                dashboard.currentPeriod.completedOrderValueCents >=
                dashboard.previousPeriod.completedOrderValueCents
                  ? 'trending_up'
                  : 'trending_down'
              }}
            </mat-icon>
            {{
              getMetricChange(
                dashboard.currentPeriod.completedOrderValueCents,
                dashboard.previousPeriod.completedOrderValueCents
              )
            }}%
            {{ 'dashboard_vs_previous_period' | translate }}
          </ng-container>
          <ng-template #newCompletedValue>
            <mat-icon>auto_awesome</mat-icon>
            {{ 'dashboard_new_activity' | translate }}
          </ng-template>
        </div>
      </article>

      <article class="kpi-card">
        <div class="kpi-icon"><mat-icon>receipt_long</mat-icon></div>
        <div class="kpi-label">{{ 'dashboard_kpi_completed_orders' | translate }}</div>
        <div class="kpi-value">{{ dashboard.currentPeriod.completedOrderCount }}</div>
        <div
          class="kpi-comparison"
          [ngClass]="getMetricTrendClass(
            dashboard.currentPeriod.completedOrderCount,
            dashboard.previousPeriod.completedOrderCount
          )"
        >
          <ng-container
            *ngIf="
              getMetricChange(
                dashboard.currentPeriod.completedOrderCount,
                dashboard.previousPeriod.completedOrderCount
              ) !== null;
              else newCompletedOrders
            "
          >
            <mat-icon>
              {{
                dashboard.currentPeriod.completedOrderCount >=
                dashboard.previousPeriod.completedOrderCount
                  ? 'trending_up'
                  : 'trending_down'
              }}
            </mat-icon>
            {{
              getMetricChange(
                dashboard.currentPeriod.completedOrderCount,
                dashboard.previousPeriod.completedOrderCount
              )
            }}%
            {{ 'dashboard_vs_previous_period' | translate }}
          </ng-container>
          <ng-template #newCompletedOrders>
            <mat-icon>auto_awesome</mat-icon>
            {{ 'dashboard_new_activity' | translate }}
          </ng-template>
        </div>
      </article>

      <article class="kpi-card">
        <div class="kpi-icon"><mat-icon>shopping_basket</mat-icon></div>
        <div class="kpi-label">{{ 'dashboard_kpi_average_order' | translate }}</div>
        <div class="kpi-value">
          {{ dashboard.currentPeriod.averageCompletedOrderValueCents | format_cents: true }}
        </div>
        <div
          class="kpi-comparison"
          [ngClass]="getMetricTrendClass(
            dashboard.currentPeriod.averageCompletedOrderValueCents,
            dashboard.previousPeriod.averageCompletedOrderValueCents
          )"
        >
          <ng-container
            *ngIf="
              getMetricChange(
                dashboard.currentPeriod.averageCompletedOrderValueCents,
                dashboard.previousPeriod.averageCompletedOrderValueCents
              ) !== null;
              else newAverageOrder
            "
          >
            <mat-icon>
              {{
                dashboard.currentPeriod.averageCompletedOrderValueCents >=
                dashboard.previousPeriod.averageCompletedOrderValueCents
                  ? 'trending_up'
                  : 'trending_down'
              }}
            </mat-icon>
            {{
              getMetricChange(
                dashboard.currentPeriod.averageCompletedOrderValueCents,
                dashboard.previousPeriod.averageCompletedOrderValueCents
              )
            }}%
            {{ 'dashboard_vs_previous_period' | translate }}
          </ng-container>
          <ng-template #newAverageOrder>
            <mat-icon>auto_awesome</mat-icon>
            {{ 'dashboard_new_activity' | translate }}
          </ng-template>
        </div>
      </article>

      <article class="kpi-card">
        <div class="kpi-icon"><mat-icon>groups</mat-icon></div>
        <div class="kpi-label">{{ 'dashboard_kpi_unique_customers' | translate }}</div>
        <div class="kpi-value">{{ dashboard.currentPeriod.uniqueCustomerCount }}</div>
        <div
          class="kpi-comparison"
          [ngClass]="getMetricTrendClass(
            dashboard.currentPeriod.uniqueCustomerCount,
            dashboard.previousPeriod.uniqueCustomerCount
          )"
        >
          <ng-container
            *ngIf="
              getMetricChange(
                dashboard.currentPeriod.uniqueCustomerCount,
                dashboard.previousPeriod.uniqueCustomerCount
              ) !== null;
              else newCustomers
            "
          >
            <mat-icon>
              {{
                dashboard.currentPeriod.uniqueCustomerCount >=
                dashboard.previousPeriod.uniqueCustomerCount
                  ? 'trending_up'
                  : 'trending_down'
              }}
            </mat-icon>
            {{
              getMetricChange(
                dashboard.currentPeriod.uniqueCustomerCount,
                dashboard.previousPeriod.uniqueCustomerCount
              )
            }}%
            {{ 'dashboard_vs_previous_period' | translate }}
          </ng-container>
          <ng-template #newCustomers>
            <mat-icon>auto_awesome</mat-icon>
            {{ 'dashboard_new_activity' | translate }}
          </ng-template>
        </div>
      </article>
    </section>

    <section class="dashboard-card performance-card">
      <div class="section-heading">
        <div>
          <span class="section-eyebrow">{{ 'dashboard_performance_eyebrow' | translate }}</span>
          <h2>{{ 'dashboard_performance_title' | translate }}</h2>
          <p>
            {{ formatDateKey(dashboard.currentPeriod.fromDate) }} &ndash;
            {{ formatDateKey(dashboard.currentPeriod.toDate) }}
          </p>
        </div>
        <div class="performance-summary">
          <span>{{ 'dashboard_received_orders' | translate }}</span>
          <strong>{{ dashboard.currentPeriod.receivedOrderCount }}</strong>
        </div>
      </div>
      <web-foodis-dashboard-performance-chart
        [series]="dashboard.dailySeries"
        [currencyCode]="dashboard.currencyCode"
      ></web-foodis-dashboard-performance-chart>
    </section>

    <section class="operations-section">
      <div class="section-heading operations-heading">
        <div>
          <span class="section-eyebrow">{{ 'dashboard_live_operations' | translate }}</span>
          <h2>{{ 'dashboard_orders_needing_you' | translate }}</h2>
          <p>{{ 'dashboard_orders_needing_you_description' | translate }}</p>
        </div>
        <a mat-stroked-button routerLink="/orders">
          {{ 'dashboard_view_all_orders' | translate }}
          <mat-icon>arrow_forward</mat-icon>
        </a>
      </div>

      <div class="queue-grid">
        <article class="dashboard-card queue-card urgent-queue">
          <div class="queue-card-heading">
            <div class="queue-title">
              <span class="attention-pulse"></span>
              <div>
                <h3>{{ 'dashboard_action_required' | translate }}</h3>
                <span>{{ 'dashboard_action_required_description' | translate }}</span>
              </div>
            </div>
            <span class="count-badge urgent">{{ dashboard.actionRequiredOrderCount }}</span>
          </div>

          <ng-container *ngIf="dashboard.actionRequiredOrders.length > 0; else noUrgentOrders">
            <a
              class="order-row"
              *ngFor="let order of dashboard.actionRequiredOrders; trackBy: trackOrder"
              [routerLink]="['/orders', order.id]"
            >
              <div class="order-main">
                <div class="order-reference">
                  <strong>#{{ order.slug }}</strong>
                  <span class="status-pill" [ngClass]="getStatusClass(order.status)">
                    {{ getStatusTranslationKey(order.status) | translate }}
                  </span>
                </div>
                <div class="order-meta">
                  <span>
                    <mat-icon>schedule</mat-icon>
                    {{ formatTimestamp(order.createdDate, dashboard.timeZone, 'time') }}
                  </span>
                  <span>
                    <mat-icon>
                      {{ order.deliveryOption === DeliveryOption.DELIVERY ? 'delivery_dining' : 'takeout_dining' }}
                    </mat-icon>
                    {{ getDeliveryOptionTranslationKey(order.deliveryOption) | translate }}
                  </span>
                  <span>
                    {{ order.itemCount }}
                    {{ (order.itemCount === 1 ? 'dashboard_item' : 'dashboard_items') | translate }}
                  </span>
                </div>
              </div>
              <div class="order-value">
                <strong>{{ order.totalPriceCents | format_cents: true }}</strong>
                <span>{{ getPaymentTypeTranslationKey(order.paymentType) | translate }}</span>
              </div>
              <mat-icon class="open-order-icon">chevron_right</mat-icon>
            </a>
          </ng-container>

          <ng-template #noUrgentOrders>
            <div class="queue-empty positive">
              <mat-icon>task_alt</mat-icon>
              <div>
                <strong>{{ 'dashboard_no_action_orders_title' | translate }}</strong>
                <span>{{ 'dashboard_no_action_orders_description' | translate }}</span>
              </div>
            </div>
          </ng-template>
        </article>

        <article class="dashboard-card queue-card">
          <div class="queue-card-heading">
            <div class="queue-title">
              <mat-icon class="queue-heading-icon">restaurant</mat-icon>
              <div>
                <h3>{{ 'dashboard_active_orders' | translate }}</h3>
                <span>{{ 'dashboard_active_orders_description' | translate }}</span>
              </div>
            </div>
            <span class="count-badge">{{ dashboard.activeOrderCount }}</span>
          </div>

          <ng-container *ngIf="dashboard.activeOrders.length > 0; else noActiveOrders">
            <a
              class="order-row"
              *ngFor="let order of dashboard.activeOrders; trackBy: trackOrder"
              [routerLink]="['/orders', order.id]"
            >
              <div class="order-main">
                <div class="order-reference">
                  <strong>#{{ order.slug }}</strong>
                  <span class="status-pill" [ngClass]="getStatusClass(order.status)">
                    {{ getStatusTranslationKey(order.status) | translate }}
                  </span>
                </div>
                <div class="order-meta">
                  <span>
                    <mat-icon>schedule</mat-icon>
                    {{ formatTimestamp(order.createdDate, dashboard.timeZone, 'time') }}
                  </span>
                  <span *ngIf="order.estimatedDeliveryTime">
                    <mat-icon>flag</mat-icon>
                    {{ 'dashboard_due' | translate }}
                    {{
                      formatTimestamp(order.estimatedDeliveryTime, dashboard.timeZone, 'time')
                    }}
                  </span>
                </div>
              </div>
              <div class="order-value">
                <strong>{{ order.totalPriceCents | format_cents: true }}</strong>
                <span>{{ getDeliveryOptionTranslationKey(order.deliveryOption) | translate }}</span>
              </div>
              <mat-icon class="open-order-icon">chevron_right</mat-icon>
            </a>
          </ng-container>

          <ng-template #noActiveOrders>
            <div class="queue-empty">
              <mat-icon>hourglass_empty</mat-icon>
              <div>
                <strong>{{ 'dashboard_no_active_orders_title' | translate }}</strong>
                <span>{{ 'dashboard_no_active_orders_description' | translate }}</span>
              </div>
            </div>
          </ng-template>
        </article>
      </div>
    </section>

    <section class="insights-grid">
      <article class="dashboard-card top-products-card">
        <div class="section-heading compact">
          <div>
            <span class="section-eyebrow">{{ 'dashboard_menu_insights' | translate }}</span>
            <h2>{{ 'dashboard_top_dishes' | translate }}</h2>
          </div>
          <mat-icon>local_fire_department</mat-icon>
        </div>

        <ng-container *ngIf="dashboard.topProducts.length > 0; else noTopProducts">
          <div
            class="product-rank"
            *ngFor="let product of dashboard.topProducts; index as index; trackBy: trackProduct"
          >
            <span class="rank-number">{{ index + 1 }}</span>
            <div class="rank-details">
              <div class="rank-label">
                <strong>{{ product.productName }}</strong>
                <span>{{ product.quantitySold }} {{ 'dashboard_sold' | translate }}</span>
              </div>
              <div class="rank-track">
                <span
                  [style.width.%]="getTopProductWidth(product.quantitySold, dashboard.topProducts)"
                ></span>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #noTopProducts>
          <div class="compact-empty">
            <mat-icon>ramen_dining</mat-icon>
            <strong>{{ 'dashboard_top_dishes_empty_title' | translate }}</strong>
            <span>{{ 'dashboard_top_dishes_empty_description' | translate }}</span>
          </div>
        </ng-template>
      </article>

      <article class="dashboard-card channel-card">
        <div class="section-heading compact">
          <div>
            <span class="section-eyebrow">{{ 'dashboard_order_mix' | translate }}</span>
            <h2>{{ 'dashboard_channels_title' | translate }}</h2>
          </div>
          <mat-icon>donut_large</mat-icon>
        </div>

        <ng-container
          *ngIf="
            getChannelCount(dashboard.orderChannelBreakdown, DeliveryOption.DELIVERY) +
              getChannelCount(dashboard.orderChannelBreakdown, DeliveryOption.PICKUP) >
              0;
            else noChannelData
          "
        >
          <div class="donut-layout">
            <div class="donut-wrapper">
              <svg
                class="channel-donut"
                viewBox="0 0 42 42"
                role="img"
                [attr.aria-label]="'dashboard_channels_aria_label' | translate"
              >
                <title>{{ 'dashboard_channels_aria_label' | translate }}</title>
                <circle class="donut-background" cx="21" cy="21" r="15.9155"></circle>
                <circle
                  class="donut-delivery"
                  cx="21"
                  cy="21"
                  r="15.9155"
                  pathLength="100"
                  [attr.stroke-dasharray]="
                    getChannelPercentage(dashboard.orderChannelBreakdown, DeliveryOption.DELIVERY) +
                    ' ' +
                    (100 -
                      getChannelPercentage(dashboard.orderChannelBreakdown, DeliveryOption.DELIVERY))
                  "
                ></circle>
              </svg>
              <div class="donut-center">
                <strong>
                  {{
                    getChannelCount(dashboard.orderChannelBreakdown, DeliveryOption.DELIVERY) +
                      getChannelCount(dashboard.orderChannelBreakdown, DeliveryOption.PICKUP)
                  }}
                </strong>
                <span>{{ 'dashboard_orders' | translate }}</span>
              </div>
            </div>

            <div class="channel-legend">
              <div>
                <span class="channel-color delivery"></span>
                <span>{{ 'dashboard_delivery_option_delivery' | translate }}</span>
                <strong>
                  {{ getChannelPercentage(dashboard.orderChannelBreakdown, DeliveryOption.DELIVERY) }}%
                </strong>
              </div>
              <div>
                <span class="channel-color pickup"></span>
                <span>{{ 'dashboard_delivery_option_pickup' | translate }}</span>
                <strong>
                  {{ getChannelPercentage(dashboard.orderChannelBreakdown, DeliveryOption.PICKUP) }}%
                </strong>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #noChannelData>
          <div class="compact-empty">
            <mat-icon>pie_chart_outline</mat-icon>
            <strong>{{ 'dashboard_channels_empty_title' | translate }}</strong>
            <span>{{ 'dashboard_channels_empty_description' | translate }}</span>
          </div>
        </ng-template>
      </article>

      <article class="dashboard-card products-card">
        <div class="section-heading compact">
          <div>
            <span class="section-eyebrow">{{ 'dashboard_menu_health' | translate }}</span>
            <h2>{{ 'dashboard_product_availability' | translate }}</h2>
          </div>
          <mat-icon>inventory_2</mat-icon>
        </div>

        <ng-container *ngIf="dashboard.products.total > 0; else noProducts">
          <div class="availability-score">
            <strong>
              {{
                getProductsAvailablePercentage(
                  dashboard.products.total,
                  dashboard.products.available
                )
              }}%
            </strong>
            <span>{{ 'dashboard_available' | translate }}</span>
          </div>
          <div
            class="availability-track"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            [attr.aria-valuenow]="
              getProductsAvailablePercentage(
                dashboard.products.total,
                dashboard.products.available
              )
            "
          >
            <span
              [style.width.%]="
                getProductsAvailablePercentage(
                  dashboard.products.total,
                  dashboard.products.available
                )
              "
            ></span>
          </div>
          <div class="availability-counts">
            <div>
              <span class="availability-dot available"></span>
              <span>{{ 'dashboard_available_products' | translate }}</span>
              <strong>{{ dashboard.products.available }}</strong>
            </div>
            <div>
              <span class="availability-dot unavailable"></span>
              <span>{{ 'dashboard_unavailable_products' | translate }}</span>
              <strong>{{ dashboard.products.unavailable }}</strong>
            </div>
          </div>
          <a mat-stroked-button routerLink="/menu">
            {{ 'dashboard_manage_menu' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </a>
        </ng-container>

        <ng-template #noProducts>
          <div class="compact-empty">
            <mat-icon>inventory_2</mat-icon>
            <strong>{{ 'dashboard_products_empty_title' | translate }}</strong>
            <span>{{ 'dashboard_products_empty_description' | translate }}</span>
            <a mat-stroked-button routerLink="/menu">{{ 'dashboard_add_products' | translate }}</a>
          </div>
        </ng-template>
      </article>
    </section>

    <section class="dashboard-card feedback-card">
      <div class="section-heading">
        <div>
          <span class="section-eyebrow">{{ 'dashboard_customer_voice' | translate }}</span>
          <h2>{{ 'dashboard_feedback_title' | translate }}</h2>
          <p>{{ 'dashboard_feedback_description' | translate }}</p>
        </div>
        <a mat-stroked-button routerLink="/reviews">
          {{ 'dashboard_view_reviews' | translate }}
          <mat-icon>arrow_forward</mat-icon>
        </a>
      </div>

      <ng-container *ngIf="dashboard.reviews.totalReviews > 0; else noReviews">
        <div class="feedback-grid">
          <div class="rating-summary">
            <div class="rating-score">
              <strong>{{ dashboard.reviews.averageRating | number: '1.1-1' }}</strong>
              <div class="rating-stars" [attr.aria-label]="'dashboard_average_rating' | translate">
                <mat-icon *ngFor="let star of starValues">
                  {{ star <= (dashboard.reviews.averageRating || 0) ? 'star' : 'star_border' }}
                </mat-icon>
              </div>
              <span>
                {{ dashboard.reviews.totalReviews }}
                {{ 'dashboard_review_count' | translate }}
              </span>
            </div>

            <div class="rating-distribution">
              <div class="rating-row" *ngFor="let rating of ratingValues">
                <span>{{ rating }} <mat-icon>star</mat-icon></span>
                <div class="rating-track">
                  <span
                    [style.width.%]="
                      getRatingPercentage(
                        rating,
                        dashboard.reviews.ratingDistribution,
                        dashboard.reviews.totalReviews
                      )
                    "
                  ></span>
                </div>
                <strong>
                  {{ getRatingCount(rating, dashboard.reviews.ratingDistribution) }}
                </strong>
              </div>
            </div>
          </div>

          <div class="recent-reviews">
            <h3>{{ 'dashboard_recent_reviews' | translate }}</h3>
            <ng-container *ngIf="dashboard.reviews.recentReviews.length > 0; else noRecentReviews">
              <a
                class="review-row"
                *ngFor="let review of dashboard.reviews.recentReviews"
                routerLink="/reviews"
              >
                <div class="review-heading">
                  <span class="review-stars">
                    <mat-icon *ngFor="let star of starValues">
                      {{ star <= review.rating ? 'star' : 'star_border' }}
                    </mat-icon>
                  </span>
                  <span>#{{ review.orderSlug }}</span>
                </div>
                <p>
                  {{ review.comment || ('reviews_page_no_comment' | translate) }}
                </p>
                <span class="review-date">
                  {{ formatTimestamp(review.createdDate, dashboard.timeZone, 'date') }}
                </span>
              </a>
            </ng-container>
            <ng-template #noRecentReviews>
              <div class="recent-reviews-empty">
                {{ 'dashboard_no_recent_reviews' | translate }}
              </div>
            </ng-template>
          </div>
        </div>
      </ng-container>

      <ng-template #noReviews>
        <div class="feedback-empty">
          <mat-icon>rate_review</mat-icon>
          <div>
            <strong>{{ 'dashboard_reviews_empty_title' | translate }}</strong>
            <span>{{ 'dashboard_reviews_empty_description' | translate }}</span>
          </div>
        </div>
      </ng-template>
    </section>
  </ng-container>

  <ng-template #initialState>
    <ng-container *ngIf="hasError$ | async; else loadingState">
      <div class="initial-error" role="alert">
        <mat-icon>cloud_off</mat-icon>
        <h2>{{ 'dashboard_error_title' | translate }}</h2>
        <p>{{ 'dashboard_error_description' | translate }}</p>
        <fs-button
          [text]="'dashboard_retry' | translate"
          [type]="'raised-primary'"
          [leadingIcon]="'refresh'"
          (onPress)="refresh()"
        ></fs-button>
      </div>
    </ng-container>

    <ng-template #loadingState>
      <div class="dashboard-loading" aria-live="polite">
        <div class="loading-heading">
          <div class="skeleton-line wide"></div>
          <div class="skeleton-line medium"></div>
        </div>
        <div class="skeleton-grid">
          <div class="skeleton-card" *ngFor="let item of [1, 2, 3, 4]">
            <div class="skeleton-line short"></div>
            <div class="skeleton-line value"></div>
            <div class="skeleton-line medium"></div>
          </div>
        </div>
        <div class="skeleton-chart"></div>
        <div class="loading-caption">
          <fs-loading-spinner [variant]="LOADING_STYLE.CIRCLE_GRAY"></fs-loading-spinner>
          {{ 'dashboard_loading' | translate }}
        </div>
      </div>
    </ng-template>
  </ng-template>
</div>
